import Link from 'next/link';
import Image from 'next/image';
import { APP_VERSION } from '@/config/version';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    product: [
      { label: 'How It Works', href: '/how-it-works' },
      { label: 'Features', href: '/features' },
      { label: 'Pricing', href: '/pricing' },
      { label: 'FAQ', href: '/faq' },
    ],
    company: [
      { label: 'Founder', href: 'https://portfolio.lazyeholdings.com', external: true },
      { label: 'Contact', href: '/#contact' },
    ],
    services: [
      { label: 'CoreOps Consulting', href: 'https://coreops-landing.vercel.app', external: true },
      { label: 'CoreOps Coordination', href: 'https://coreops-landing.vercel.app#coordination', external: true },
    ],
    legal: [
      { label: 'Privacy Policy', href: '/privacy' },
      { label: 'Terms of Service', href: '/terms' },
      { label: 'Cookie Policy', href: '/cookies' },
    ],
  };

  return (
    <footer className="relative border-t border-gray-200 dark:border-white/[0.06]">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-gray-50 to-white dark:from-[#0a0a0a] dark:to-[#0d0d0d]" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-16 sm:py-20">
        {/* Top section — Brand + Link columns side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 mb-12">
          {/* Brand column — takes more space */}
          <div className="lg:col-span-4">
            <Link href="/" className="inline-flex items-center gap-4 mb-6">
              <Image
                src="/images/axis-datum-compass.png"
                alt="Axis Datum"
                width={56}
                height={56}
                className="w-14 h-14 object-contain"
              />
              <div className="flex flex-col leading-none">
                <span className="text-3xl font-brand italic font-semibold text-gray-900 dark:text-white tracking-wide">
                  Axis
                </span>
                <span className="text-sm font-medium tracking-[0.2em] text-gray-400 dark:text-secondary-400 self-end -mt-0.5 ml-4">
                  datum
                </span>
              </div>
            </Link>

            <p className="text-xl font-brand italic text-gray-500 dark:text-secondary-400 mb-6 leading-relaxed">
              Where Operations Align.
            </p>

            <div className="flex items-center gap-4 mb-6">
              <div className="px-2.5 py-1 rounded-md bg-primary-500/10 border border-primary-500/20">
                <span className="text-xs text-primary-600 dark:text-primary-400 font-mono">v{APP_VERSION}</span>
              </div>
              <span className="text-xs text-gray-400 dark:text-secondary-600">Veteran-Owned (SDVOSB)</span>
            </div>

            {/* Social */}
            <div className="flex items-center gap-3">
              <a
                href="https://linkedin.com/in/ctlilley"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center w-10 h-10 rounded-lg text-gray-400 dark:text-secondary-500 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors"
                aria-label="LinkedIn"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Link columns */}
          <div className="lg:col-span-8 grid grid-cols-2 sm:grid-cols-4 gap-8 lg:gap-12">
            {/* Product */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-secondary-100 uppercase tracking-wider mb-5">
                Product
              </h4>
              <ul className="space-y-3.5">
                {footerLinks.product.map((link) => (
                  <li key={link.label}>
                    <Link
                      href={link.href}
                      className="text-sm text-gray-500 dark:text-secondary-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-secondary-100 uppercase tracking-wider mb-5">
                Company
              </h4>
              <ul className="space-y-3.5">
                {footerLinks.company.map((link) => (
                  <li key={link.label}>
                    {'external' in link && link.external ? (
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-gray-500 dark:text-secondary-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                      >
                        {link.label}
                      </a>
                    ) : (
                      <Link
                        href={link.href}
                        className="text-sm text-gray-500 dark:text-secondary-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                      >
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* Services */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-secondary-100 uppercase tracking-wider mb-5">
                Services
              </h4>
              <ul className="space-y-3.5">
                {footerLinks.services.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-gray-500 dark:text-secondary-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-secondary-100 uppercase tracking-wider mb-5">
                Legal
              </h4>
              <ul className="space-y-3.5">
                {footerLinks.legal.map((link) => (
                  <li key={link.label}>
                    <Link
                      href={link.href}
                      className="text-sm text-gray-500 dark:text-secondary-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-white/[0.08] to-transparent mb-8" />

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-sm text-gray-400 dark:text-secondary-600">
            &copy; {currentYear} Lazy E Holdings LLC. All rights reserved.
          </div>
          <div className="text-xs text-gray-300 dark:text-secondary-700">
            Veteran-Owned Small Business (SDVOSB)
          </div>
        </div>
      </div>
    </footer>
  );
};

export { Footer };
